import { useEffect, useMemo, useState } from 'react';
import { Link, useParams } from 'react-router-dom';
import { ChevronLeft, Eye, MessageSquare, Pencil, Trash2, X, Check } from 'lucide-react';
import { z } from 'zod';
import { Layout } from '@/components/layout/Layout';
import { SEOHead } from '@/components/SEOHead';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
  AlertDialogTrigger,
} from '@/components/ui/alert-dialog';
import { useToast } from '@/hooks/use-toast';
import {
  FORUM_CATEGORIES,
  addReply,
  avatarFor,
  deleteReply,
  getTopic,
  incrementViews,
  isOwnedReply,
  isOwnedTopic,
  relativeTime,
  updateReply,
  updateTopic,
  useForumTopics,
  useTopicReplies,
} from '@/lib/forumStore';
import { clearDraft, loadDraft, useAutosaveDraft } from '@/lib/forumDrafts';
import { Markdown } from '@/components/forum/Markdown';
import { MarkdownEditor } from '@/components/forum/MarkdownEditor';

const replySchema = z
  .string()
  .trim()
  .min(2, 'Reply must be at least 2 characters')
  .max(4000, 'Reply is too long');

const editSchema = z.object({
  body: z.string().trim().max(4000, 'Body is too long'),
  category: z.string().min(1, 'Pick a category'),
  tags: z.array(z.string().max(40)).max(5, 'Up to 5 tags allowed'),
});

export default function ForumTopic() {
  const { id = '' } = useParams();
  const { toast } = useToast();
  useForumTopics();
  const topic = useMemo(() => getTopic(id), [id]);
  const replies = useTopicReplies(id);

  const draftKey = `ere_forum_draft_reply_${id}`;
  const [body, setBody] = useState<string>(() => loadDraft<string>(draftKey) || '');
  const [submitting, setSubmitting] = useState(false);
  useAutosaveDraft(draftKey, body, (v) => v.trim().length === 0);

  const [editing, setEditing] = useState(false);
  const [editBody, setEditBody] = useState('');
  const [editCategory, setEditCategory] = useState('');
  const [editTags, setEditTags] = useState('');

  const [editingReplyId, setEditingReplyId] = useState<string | null>(null);
  const [editReplyBody, setEditReplyBody] = useState('');

  useEffect(() => {
    if (topic) incrementViews(topic.id);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [id]);

  if (!topic) {
    return (
      <Layout>
        <SEOHead title="Topic not found — Ethio Real Estates" description="The forum topic you requested could not be found." canonical={`/forum/${id}`} />
        <section className="container py-16">
          <div className="rounded-xl border bg-card p-10 text-center max-w-lg mx-auto">
            <h1 className="font-display text-2xl font-bold mb-2">Topic not found</h1>
            <p className="text-sm text-muted-foreground mb-6">
              The topic you're looking for doesn't exist or was removed.
            </p>
            <Button asChild>
              <Link to="/forum">
                <ChevronLeft className="h-4 w-4" /> Back to forum
              </Link>
            </Button>
          </div>
        </section>
      </Layout>
    );
  }

  const cat = FORUM_CATEGORIES.find((c) => c.slug === topic.category);
  const author = topic.participants[0] || avatarFor('U');
  const ownsTopic = isOwnedTopic(topic.id);

  const startEditing = () => {
    setEditBody(topic.body || '');
    setEditCategory(topic.category);
    setEditTags(topic.tags.join(', '));
    setEditing(true);
  };

  const saveEdit = () => {
    const tagsArr = editTags.split(',').map((t) => t.trim()).filter(Boolean).slice(0, 5);
    const result = editSchema.safeParse({ body: editBody, category: editCategory, tags: tagsArr });
    if (!result.success) {
      toast({ title: 'Cannot save', description: result.error.errors[0].message, variant: 'destructive' });
      return;
    }
    updateTopic(topic.id, result.data);
    setEditing(false);
    toast({ title: 'Topic updated' });
  };

  const handleSubmit = () => {
    const result = replySchema.safeParse(body);
    if (!result.success) {
      toast({ title: 'Cannot post reply', description: result.error.errors[0].message, variant: 'destructive' });
      return;
    }
    setSubmitting(true);
    addReply({ topicId: topic.id, body: result.data, author: 'You' });
    setBody('');
    clearDraft(draftKey);
    setSubmitting(false);
    toast({ title: 'Reply posted' });
    setTimeout(() => {
      document.getElementById('replies-end')?.scrollIntoView({ behavior: 'smooth', block: 'center' });
    }, 50);
  };

  const handleDeleteReply = (replyId: string) => {
    deleteReply(replyId);
    if (editingReplyId === replyId) setEditingReplyId(null);
    toast({ title: 'Reply deleted' });
  };

  const startEditReply = (replyId: string, current: string) => {
    setEditingReplyId(replyId);
    setEditReplyBody(current);
  };

  const saveReplyEdit = () => {
    if (!editingReplyId) return;
    const result = replySchema.safeParse(editReplyBody);
    if (!result.success) {
      toast({ title: 'Cannot save', description: result.error.errors[0].message, variant: 'destructive' });
      return;
    }
    updateReply(editingReplyId, result.data);
    setEditingReplyId(null);
    setEditReplyBody('');
    toast({ title: 'Reply updated' });
  };

  return (
    <Layout>
      <SEOHead title={`${topic.title} — Forum`} description={topic.title} canonical={`/forum/${topic.id}`} />

      <section className="container py-8 max-w-4xl">
        <Link to="/forum" className="inline-flex items-center gap-1 text-sm text-muted-foreground hover:text-primary mb-4">
          <ChevronLeft className="h-4 w-4" /> Back to forum
        </Link>

        <article className="rounded-xl border bg-card p-6 mb-6">
          <div className="flex items-start justify-between gap-3 mb-3">
            <div className="flex items-center gap-2 flex-wrap">
              {!editing && cat && (
                <span className={`inline-flex items-center text-[11px] font-semibold px-2 py-0.5 rounded text-white ${cat.color}`}>
                  {cat.name}
                </span>
              )}
              {!editing && topic.tags.map((tag) => (
                <span key={tag} className="text-[11px] text-muted-foreground bg-muted px-1.5 py-0.5 rounded">
                  #{tag}
                </span>
              ))}
            </div>
            {ownsTopic && !editing && (
              <Button variant="outline" size="sm" onClick={startEditing} className="shrink-0">
                <Pencil className="h-3.5 w-3.5" /> Edit
              </Button>
            )}
          </div>

          <h1 className="font-display text-2xl md:text-3xl font-bold mb-4 leading-tight">{topic.title}</h1>

          <div className="flex items-center gap-3 text-xs text-muted-foreground mb-5 pb-5 border-b">
            <span className={`h-7 w-7 rounded-full ${author.color} text-white text-[11px] font-bold flex items-center justify-center`}>
              {author.initial}
            </span>
            <span>Posted {relativeTime(topic.createdAt)} ago</span>
            <span className="inline-flex items-center gap-1"><MessageSquare className="h-3.5 w-3.5" /> {topic.replies}</span>
            <span className="inline-flex items-center gap-1"><Eye className="h-3.5 w-3.5" /> {topic.views}</span>
          </div>

          {editing ? (
            <div className="space-y-3">
              <div>
                <label className="text-xs font-semibold mb-1 block">Category</label>
                <Select value={editCategory} onValueChange={setEditCategory}>
                  <SelectTrigger><SelectValue /></SelectTrigger>
                  <SelectContent>
                    {FORUM_CATEGORIES.map((c) => (
                      <SelectItem key={c.slug} value={c.slug}>{c.name}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              <div>
                <label className="text-xs font-semibold mb-1 block">
                  Tags <span className="text-muted-foreground font-normal">(comma separated, up to 5)</span>
                </label>
                <Input value={editTags} onChange={(e) => setEditTags(e.target.value)} placeholder="e.g. addis-ababa, mortgage" />
              </div>
              <div>
                <label className="text-xs font-semibold mb-1 block">Body</label>
                <MarkdownEditor value={editBody} onChange={setEditBody} rows={8} maxLength={4000} />
              </div>
              <div className="flex justify-end gap-2 pt-1">
                <Button variant="outline" onClick={() => setEditing(false)}>
                  <X className="h-4 w-4" /> Cancel
                </Button>
                <Button onClick={saveEdit}>Save changes</Button>
              </div>
            </div>
          ) : topic.body ? (
            <Markdown>{topic.body}</Markdown>
          ) : (
            <p className="text-sm text-muted-foreground italic">No additional details were shared.</p>
          )}
        </article>

        <div className="mb-6">
          <h2 className="font-display text-lg font-bold mb-3">
            {replies.length} {replies.length === 1 ? 'Reply' : 'Replies'}
          </h2>

          {replies.length === 0 ? (
            <div className="rounded-xl border bg-card p-6 text-center text-sm text-muted-foreground">
              No replies yet — be the first to respond.
            </div>
          ) : (
            <ul className="space-y-3">
              {replies.map((r) => {
                const av = avatarFor(r.author);
                const ownsReply = isOwnedReply(r.id);
                return (
                  <li key={r.id} className="rounded-xl border bg-card p-4">
                    <div className="flex items-center justify-between mb-2">
                      <div className="flex items-center gap-2">
                        <span className={`h-6 w-6 rounded-full ${av.color} text-white text-[10px] font-bold flex items-center justify-center`}>
                          {av.initial}
                        </span>
                        <span className="text-sm font-semibold">{r.author}</span>
                        <span className="text-xs text-muted-foreground">· {relativeTime(r.createdAt)} ago</span>
                      </div>
                      {ownsReply && editingReplyId !== r.id && (
                        <div className="flex items-center gap-1">
                          <Button variant="ghost" size="icon" className="h-7 w-7 text-muted-foreground hover:text-primary" aria-label="Edit reply" onClick={() => startEditReply(r.id, r.body)}>
                            <Pencil className="h-3.5 w-3.5" />
                          </Button>
                          <AlertDialog>
                            <AlertDialogTrigger asChild>
                              <Button variant="ghost" size="icon" className="h-7 w-7 text-muted-foreground hover:text-destructive" aria-label="Delete reply">
                                <Trash2 className="h-3.5 w-3.5" />
                              </Button>
                            </AlertDialogTrigger>
                            <AlertDialogContent>
                              <AlertDialogHeader>
                                <AlertDialogTitle>Delete this reply?</AlertDialogTitle>
                                <AlertDialogDescription>
                                  This action cannot be undone. The reply will be permanently removed and the replies count updated.
                                </AlertDialogDescription>
                              </AlertDialogHeader>
                              <AlertDialogFooter>
                                <AlertDialogCancel>Cancel</AlertDialogCancel>
                                <AlertDialogAction onClick={() => handleDeleteReply(r.id)}>Delete</AlertDialogAction>
                              </AlertDialogFooter>
                            </AlertDialogContent>
                          </AlertDialog>
                        </div>
                      )}
                    </div>
                    {editingReplyId === r.id ? (
                      <div className="space-y-2">
                        <MarkdownEditor value={editReplyBody} onChange={setEditReplyBody} rows={4} maxLength={4000} />
                        <div className="flex justify-end gap-2">
                          <Button variant="outline" size="sm" onClick={() => { setEditingReplyId(null); setEditReplyBody(''); }}>
                            <X className="h-4 w-4" /> Cancel
                          </Button>
                          <Button size="sm" onClick={saveReplyEdit}>
                            <Check className="h-4 w-4" /> Save
                          </Button>
                        </div>
                      </div>
                    ) : (
                      <Markdown>{r.body}</Markdown>
                    )}
                  </li>
                );
              })}
            </ul>
          )}
          <div id="replies-end" />
        </div>

        <div className="rounded-xl border bg-card p-4">
          <label className="block text-sm font-semibold mb-2">Write a reply</label>
          <MarkdownEditor
            value={body}
            onChange={setBody}
            rows={5}
            maxLength={4000}
            placeholder="Share your thoughts… use the toolbar to format or upload an image."
          />
          <div className="flex items-center justify-between mt-3 gap-2">
            <span className="text-[11px] text-muted-foreground">
              {body.trim().length > 0 ? 'Draft autosaved on this device.' : ''}
            </span>
            <div className="flex items-center gap-2">
              {body.trim().length > 0 && (
                <Button variant="ghost" size="sm" onClick={() => { setBody(''); clearDraft(draftKey); }}>
                  Discard draft
                </Button>
              )}
              <Button onClick={handleSubmit} disabled={submitting || body.trim().length < 2}>
                Post reply
              </Button>
            </div>
          </div>
        </div>
      </section>
    </Layout>
  );
}
